import { MsalProvider } from "@azure/msal-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import App from "./App.tsx";
import { msalInitPromise, msalInstance } from "./lib/config/authConfig.ts";
import queryClient from "./lib/config/queryClient.ts";
import store from "./lib/store/dynamicStore.ts";
import "./index.css";
import TBHLogo from "@/einstein/components/common/TBHLogo.tsx";
import "./app.scss";
import { Toaster } from "react-hot-toast";
import { initializeAppInsights } from "./lib/config/appInsights.ts";

// --- Application Insights Setup ---
initializeAppInsights();

const shouldEnableReactScan =
	import.meta.env.DEV && import.meta.env.VITE_ENABLE_REACT_SCAN === "true";
// --- End Application Insights Setup ---

const container = document.getElementById("root");

const deploymentEnv = import.meta.env.VITE_CUSTOM_ENV_NAME;

// Initialize the app after MSAL is ready
const initializeApp = async () => {
	if (shouldEnableReactScan) {
		const { scan } = await import("react-scan");
		scan({ enabled: true });
	}

	// Wait for MSAL to initialize
	await msalInitPromise;

	if (container) {
		const root = createRoot(container);
		root.render(
			<React.StrictMode>
				<QueryClientProvider client={queryClient}>
					<MsalProvider instance={msalInstance}>
						<Toaster
							position="top-right"
							containerStyle={{ top: "100px" }}
							toastOptions={{
								style: {
									borderRadius: "24px",
									padding: "10px",
									fontSize: "16px",
									fontFamily: "Rubik",
								},
							}}
						/>
						<Provider store={store}>
							<BrowserRouter>
								<NuqsAdapter>
									<QueryParamProvider adapter={ReactRouter6Adapter}>
										<App />
										<TBHLogo />
									</QueryParamProvider>
								</NuqsAdapter>
							</BrowserRouter>
						</Provider>
					</MsalProvider>
					{deploymentEnv === "development" && (
						<ReactQueryDevtools initialIsOpen={false} position="bottom" />
					)}
				</QueryClientProvider>
			</React.StrictMode>,
		);
	}
};

// Start the application
initializeApp();
